import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { ROUTES } from "@routing/routes";
import { ErrorState, Spinner } from "@common/index";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { categoriesAtom } from "@/store/categoryAtoms";

// /c/browse/categories (#401 B1): every category, two levels deep, each a
// link to its browse page. Fed by the cached tree (categoriesAtom) — loaded
// here if nobody else has yet.
export const BrowseAllCategories = () => {
    const categories = useAtomValue(categoriesAtom);
    const { getCategories } = useCategoryApi();
    // Both outcomes are tracked locally rather than derived from the atoms.
    //
    // "Settled", because a loaded-but-empty tree leaves the atoms exactly as
    // they were before the read was issued, and the page must tell those two
    // apart (spinner vs "No categories yet"). It only matters while the tree
    // is empty, and it starts false: until a read of ours has answered, an
    // empty tree is "still loading", never "no categories".
    //
    // "Failed", because categoriesFetchErrorAtom is written by six call sites
    // and cleared only when a request starts. Reading it here let a failure
    // anywhere else in the session paint this page's error state over a
    // healthy in-flight read — and left a successful-but-empty tree showing
    // "Couldn't load categories." permanently, since the stale true kept
    // winning over hasSettled. This page decides from its own read.
    // (#401 review R7.)
    const [hasSettled, setHasSettled] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);

    const load = useCallback(() => {
        setHasSettled(false);
        setHasFailed(false);
        getCategories()
            .then(() => setHasSettled(true))
            .catch(() => {
                // useApi surfaces the toast; this drives the page.
                setHasFailed(true);
            });
    }, [getCategories]);

    useEffect(() => {
        if (categories.length > 0) return;
        load();
        // Once, on mount: a loaded-empty tree must not re-issue the read.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="browse-all-categories" className="bg-base-100">
            <div className="max-w-7xl mx-auto px-4 py-6 md:py-8 flex flex-col gap-6">
                <h1 className="text-2xl md:text-3xl font-bold text-neutral">
                    All Categories
                </h1>

                {categories.length > 0 ? (
                    <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {categories.map((category) => (
                            <li key={category.id}>
                                <Link
                                    to={ROUTES.BROWSE_CATEGORY(category.id)}
                                    className="text-lg font-bold text-primary hover:underline"
                                >
                                    {category.name}
                                </Link>
                                {category.children.length > 0 && (
                                    <ul className="mt-2 flex flex-col gap-1">
                                        {category.children.map((child) => (
                                            <li key={child.id}>
                                                <Link
                                                    to={ROUTES.BROWSE_CATEGORY(
                                                        child.id,
                                                    )}
                                                    className="text-neutral hover:underline"
                                                >
                                                    {child.name}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : hasFailed ? (
                    <ErrorState
                        message="Couldn't load categories."
                        onRetry={load}
                    />
                ) : hasSettled ? (
                    <p className="text-neutral/70">No categories yet.</p>
                ) : (
                    <div className="flex justify-center py-12">
                        <Spinner size="lg" />
                    </div>
                )}
            </div>
        </div>
    );
};
